import * as React from "react";
import { useMemo, useRef, useState } from "react";
import { ArrowLeft, Download, FileText } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { downloadFile, triggerBrowserDownload } from "src/api/consolidator";
import { FileDetailPanel } from "@/components/vault/FileDetailPanel";
import { VaultAriPanel } from "@/components/vault/VaultAriPanel";
import { VaultFileGrid } from "@/components/vault/VaultFileGrid";
import { VaultLayout } from "@/components/vault/VaultLayout";
import { VaultSelectionBar } from "@/components/vault/VaultSelectionBar";
import { VaultSidebar } from "@/components/vault/VaultSidebar";
import { VaultToolbar } from "@/components/vault/VaultToolbar";
import LoadingBar from "@/components/Reusable/LoadingBar";
import PDFReader from "@/components/Reusable/PDFViewer";
import { Button } from "@/components/ui";
import { useDemoOrg } from "@/contexts/DemoOrgContext";
import { useOrg } from "@/contexts/OrgContext";
import { useIsDemo } from "@/hooks/common/useIsDemo";
import { useVaultData, useDemoVaultData } from "@/hooks/vault";
import { useDemoDealsDocuments } from "@/hooks/deals/useDemoDealsDocuments";
import {
  buildBreadcrumbs,
  filterByNav,
  filterBySmartFolder,
  getSourceRoot,
  resolveFolderAtPath,
  resolvePathFolderNames,
  countFiles,
} from "@/lib/vault/vaultFileUtils";
import type { CreateFileInput, FileIconType, VaultFileItem, VaultFolderNode } from "@/types/vault.types";
import { isFolder } from "@/types/vault.types";
import type { VaultNavId, VaultSortId, VaultSourceId, VaultViewMode } from "@/types/vaultNav.types";

const VaultPage = () => {
  const isDemo = useIsDemo();
  const [searchParams] = useSearchParams();
  const dealId = searchParams.get("deal");

  const { selectedOrgId } = useOrg();
  const demoOrgContext = useDemoOrg();
  const activeOrgId = isDemo
    ? demoOrgContext?.selectedOrg?.orgId ?? "demo-personal-workspace"
    : selectedOrgId;
  const hasActiveWorkspace = isDemo
    ? Boolean(demoOrgContext?.selectedOrg || demoOrgContext?.isPersonalMode)
    : Boolean(selectedOrgId);

  const vaultData = useVaultData(isDemo ? undefined : selectedOrgId);
  const { demoDeals } = useDemoDealsDocuments();
  const {
    dealFilesMap: vaultDealFilesMap,
    dropboxRoot: vaultDropboxRoot,
    equaliqRoot: vaultEqualiqRoot,
    gdriveRoot: vaultGdriveRoot,
  } = useDemoVaultData();

  const noopFile = async (_input: CreateFileInput) => {};

  const { fileTree, deals, dealFilesMap, isLoading, error, createFile, deleteFile } = isDemo
    ? {
        fileTree: vaultEqualiqRoot,
        deals: demoDeals.map((d) => ({
          id: d.id,
          name: d.name,
          type: d.type,
          status: d.status,
          value: d.value,
        })),
        dealFilesMap: vaultDealFilesMap,
        isLoading: false,
        error: null,
        createFile: noopFile,
        deleteFile: async (_id: string) => {},
      }
    : vaultData;

  const [activeNav, setActiveNav] = useState<VaultNavId>("all");
  const [activeSource, setActiveSource] = useState<VaultSourceId>("eq");
  const [folderPath, setFolderPath] = useState<string[]>([]);
  const [activeSmartFolder, setActiveSmartFolder] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<VaultViewMode>("grid");
  const [contentSearch, setContentSearch] = useState("");
  const [sortBy, setSortBy] = useState<VaultSortId>("modified");
  const [aiOpen, setAiOpen] = useState(true);
  const [selectedFile, setSelectedFile] = useState<VaultFileItem | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<{
    fileId: string;
    url: string;
    fileName: string;
    fileType: FileIconType;
    loading: boolean;
    error?: string;
  } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const sourceRoot = useMemo(
    () =>
      getSourceRoot(
        activeSource === "all" ? "eq" : activeSource,
        fileTree,
        isDemo ? vaultGdriveRoot : null,
        isDemo ? vaultDropboxRoot : null,
      ),
    [activeSource, fileTree, isDemo, vaultGdriveRoot, vaultDropboxRoot],
  );

  const sourceLabel = useMemo(() => {
    if (activeSource === "gdrive") return "Google Drive";
    if (activeSource === "dropbox") return "Dropbox";
    return "Equal IQ Vault";
  }, [activeSource]);

  const { folders, files, breadcrumbs } = useMemo(() => {
    if (dealId) {
      const deal = deals.find((d) => d.id === dealId);
      let dealFiles = [...(dealFilesMap[dealId] ?? [])];
      if (contentSearch.trim()) {
        const q = contentSearch.toLowerCase();
        dealFiles = dealFiles.filter((f) => f.name.toLowerCase().includes(q));
      }
      dealFiles = filterBySmartFolder(dealFiles, activeSmartFolder);
      const { files: navFiles } = filterByNav([], dealFiles, activeNav, isDemo);
      const sorted = [...navFiles].sort((a, b) =>
        sortBy === "name" ? a.name.localeCompare(b.name) : b.date.localeCompare(a.date),
      );
      return {
        folders: [] as VaultFolderNode[],
        files: sorted,
        breadcrumbs: buildBreadcrumbs(sourceLabel, [], fileTree, [{ label: deal?.name ?? "Deal" }]),
      };
    }

    if (!sourceRoot) {
      return { folders: [], files: [], breadcrumbs: [{ label: "Vault", path: [] }] };
    }

    const folder =
      folderPath.length > 0 ? resolveFolderAtPath(sourceRoot, folderPath) : sourceRoot;
    if (!folder) {
      return { folders: [], files: [], breadcrumbs: [{ label: "Vault", path: [] }] };
    }

    let childFolders = folder.children.filter(isFolder) as VaultFolderNode[];
    let childFiles = folder.children.filter((c) => !isFolder(c)) as VaultFileItem[];

    if (contentSearch.trim()) {
      const q = contentSearch.toLowerCase();
      childFolders = childFolders.filter((f) => f.name.toLowerCase().includes(q));
      childFiles = childFiles.filter((f) => f.name.toLowerCase().includes(q));
    }

    childFiles = filterBySmartFolder(childFiles, activeSmartFolder);
    const filtered = filterByNav(childFolders, childFiles, activeNav, isDemo);
    childFolders = filtered.folders;
    childFiles = filtered.files;

    childFiles = [...childFiles].sort((a, b) =>
      sortBy === "name" ? a.name.localeCompare(b.name) : b.date.localeCompare(a.date),
    );

    return {
      folders: childFolders,
      files: childFiles,
      breadcrumbs: buildBreadcrumbs(sourceLabel, folderPath, sourceRoot),
    };
  }, [
    dealId,
    deals,
    dealFilesMap,
    sourceRoot,
    folderPath,
    contentSearch,
    activeSmartFolder,
    activeNav,
    isDemo,
    sortBy,
    sourceLabel,
    fileTree,
  ]);

  const totalFileCount = useMemo(() => countFiles(fileTree), [fileTree]);

  React.useEffect(() => {
    setSelectedFile(null);
    setSelectedIds(new Set());
    setPreview((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
  }, [activeNav, activeSource, folderPath, activeSmartFolder, dealId]);

  const handleBreadcrumbClick = (path: string[]) => {
    setFolderPath(path);
  };

  const handleFolderOpen = (folderId: string) => {
    setFolderPath((p) => [...p, folderId]);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleUploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploaded = event.target.files?.[0];
    if (!uploaded) return;
    if (!activeOrgId) {
      toast.error("Select an organization to upload files");
      return;
    }
    const folderNames = resolvePathFolderNames(fileTree, folderPath);
    try {
      await createFile({
        orgId: activeOrgId,
        fileName: uploaded.name,
        sizeBytes: uploaded.size,
        fileType: uploaded.type || "application/octet-stream",
        folderPath: folderNames.length > 0 ? folderNames.join("/") : undefined,
        requestUploadUrl: true,
      });
      toast.success("File added to vault");
    } catch {
      toast.error("Failed to add file");
    } finally {
      event.target.value = "";
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    try {
      await deleteFile(fileId);
      setSelectedFile(null);
      toast.success("File deleted");
    } catch {
      toast.error("Failed to delete file");
    }
  };

  const handleViewFile = async (fileId: string, fileName: string, fileType: FileIconType) => {
    setPreview((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return { fileId, url: "", fileName, fileType, loading: true };
    });
    const file = files.find((f) => f.id === fileId);
    if (file) setSelectedFile(file);

    const result = await downloadFile(fileId, fileName);
    if (result.ok) {
      const blobUrl = URL.createObjectURL(result.blob);
      setPreview((prev) =>
        prev?.fileId === fileId ? { ...prev, url: blobUrl, loading: false } : prev,
      );
    } else {
      setPreview((prev) =>
        prev?.fileId === fileId ? { ...prev, loading: false, error: "Could not load file." } : prev,
      );
    }
  };

  const handleDownloadFile = async (fileId: string, fileName: string) => {
    const result = await downloadFile(fileId, fileName);
    if (result.ok) {
      triggerBrowserDownload(result.blob, result.fileName);
    } else {
      toast.error("Download failed");
    }
  };

  if (!hasActiveWorkspace) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md rounded-xl border border-border bg-card px-6 py-8 text-center">
          <h2 className="text-lg font-semibold text-foreground">No organization selected</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Select an organization to view and manage files in Vault.
          </p>
        </div>
      </div>
    );
  }

  const hasData = fileTree.children.length > 0 || deals.length > 0;
  const showLoading = isLoading && !hasData;
  const showError = !isLoading && error && !hasData;

  const rightPanel =
    !preview && selectedFile ? (
      <FileDetailPanel
        file={selectedFile}
        onClose={() => setSelectedFile(null)}
        onView={() => handleViewFile(selectedFile.id, selectedFile.name, selectedFile.fileIcon)}
        onDownload={() => handleDownloadFile(selectedFile.id, selectedFile.name)}
        onDelete={() => void handleDeleteFile(selectedFile.id)}
      />
    ) : !preview && aiOpen ? (
      <VaultAriPanel onClose={() => setAiOpen(false)} />
    ) : null;

  return (
    <VaultLayout
      sidebar={
        <VaultSidebar
          fileCount={totalFileCount}
          activeNav={activeNav}
          activeSource={activeSource}
          activeSmartFolder={activeSmartFolder}
          onNavChange={(nav) => {
            setActiveNav(nav);
            setFolderPath([]);
          }}
          onSourceChange={(source) => {
            setActiveSource(source);
            setFolderPath([]);
          }}
          onSmartFolderChange={setActiveSmartFolder}
          onNewFolder={() => toast.info("New folder is coming soon")}
          onUpload={handleUploadClick}
          isDemo={isDemo}
        />
      }
      main={
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={(e) => void handleUploadFile(e)}
            aria-label="Upload file"
          />

          {preview ? (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-2.5">
                <div className="flex min-w-0 items-center gap-3">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => {
                      if (preview.url) URL.revokeObjectURL(preview.url);
                      setPreview(null);
                    }}
                    aria-label="Back to files"
                  >
                    <ArrowLeft className="size-4" />
                  </Button>
                  <span className="truncate text-sm font-medium">{preview.fileName}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void handleDownloadFile(preview.fileId, preview.fileName)}
                >
                  <Download className="size-3.5" aria-hidden="true" />
                  Download
                </Button>
              </div>
              <div className="min-h-0 flex-1 bg-neutral-900">
                {preview.loading && (
                  <div className="flex h-full items-center justify-center">
                    <LoadingBar />
                  </div>
                )}
                {preview.error && (
                  <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
                    <FileText className="size-12 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">{preview.error}</p>
                  </div>
                )}
                {!preview.loading && !preview.error && preview.fileType === "pdf" && (
                  <PDFReader pdfUrl={preview.url} />
                )}
                {!preview.loading && !preview.error && preview.fileType === "image" && (
                  <div className="flex h-full items-center justify-center p-6">
                    <img src={preview.url} alt={preview.fileName} className="max-h-full max-w-full object-contain" />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <VaultToolbar
                breadcrumbs={breadcrumbs}
                onBreadcrumbClick={handleBreadcrumbClick}
                search={contentSearch}
                onSearchChange={setContentSearch}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                sortBy={sortBy}
                onSortChange={setSortBy}
                aiOpen={aiOpen}
                onAiToggle={() => setAiOpen((v) => !v)}
              />
              {selectedIds.size > 0 && (
                <VaultSelectionBar count={selectedIds.size} onClear={() => setSelectedIds(new Set())} />
              )}
              {showLoading ? (
                <div className="flex flex-1 items-center justify-center">
                  <LoadingBar />
                </div>
              ) : showError ? (
                <div className="flex flex-1 flex-col items-center justify-center text-muted-foreground">
                  <p className="text-sm font-medium">Unable to load files</p>
                </div>
              ) : (
                <div className="">
                  <VaultFileGrid
                    folders={folders}
                    files={files}
                    viewMode={viewMode}
                    selectedIds={selectedIds}
                    onToggleSelect={toggleSelect}
                    onFolderOpen={handleFolderOpen}
                    onFileOpen={(file) => {
                      setSelectedFile(file);
                      setAiOpen(false);
                    }}
                    selectedFileId={selectedFile?.id}
                  />
                </div>
              )}
            </>
          )}
        </div>
      }
      rightPanel={rightPanel}
    />
  );
};

export default VaultPage;
